/**
 * 영상 CSV → 가져오기 묶음 (261103-video-production-plan). 숏폼(FLOW_대량)에 올리던 CSV 그대로 읽는다:
 * `clientId, 업체명, 주제, 카테고리, 검색문장1~3, 발행시점, 주제_260930, 이미 사용한 주제1~3`.
 * 같은 업체가 여러 줄(여러 파일을 이어 붙임)이면 프로필은 **마지막 줄**, 주제는 전부 모은다.
 * ★ clientId 가 없는 줄은 건너뛴다 — 업체를 이름으로 맞추지 않는다(동명 업체가 있다).
 */
import { MAX_SEARCH_PHRASES, type VideoImportItem } from '@geo-admin/shared/contract/api/video.ts';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** RFC 4180 표 — 따옴표 안의 쉼표·줄바꿈을 지킨다. 빈 줄은 버린다 */
export function parseTable(text: string): string[][] {
  const src = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const delim = (src.split('\n', 1)[0] ?? '').includes('\t') ? '\t' : ',';
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell.trim() === '') {
      quoted = true;
      cell = '';
    } else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      out.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    out.push(row);
  }
  return out.filter((r) => r.some((c) => c.trim() !== ''));
}

type Col = 'client' | 'business' | 'topic' | 'used' | 'category' | 'phrase';

function classify(h: string): Col | null {
  const k = h.replace(/\s+/g, '').toLowerCase();
  if (/^(clientid|client_id|고객id)$/.test(k)) return 'client';
  if (/^(업체명|업체|상호|병원명)$/.test(k)) return 'business';
  if (/^(주제|topic)$/.test(k)) return 'topic';
  if (/^(주제_\d+|이미사용한주제\d*|사용한주제\d*)$/.test(k)) return 'used';
  if (/^(카테고리|분야|업종|category)$/.test(k)) return 'category';
  if (/^(검색문장|검색어|키워드)\d*$/.test(k)) return 'phrase';
  return null;
}

export interface ParsedImport {
  items: VideoImportItem[];
  /** 읽은 줄 수 · 건너뛴 줄과 이유 */
  rows: number;
  problems: string[];
}

export function parseVideoCsv(texts: string[]): ParsedImport {
  const byBrand = new Map<string, { profile: VideoImportItem['profile']; topics: string[] }>();
  const problems: string[] = [];
  let count = 0;
  for (const [f, text] of texts.entries()) {
    const table = parseTable(text);
    if (table.length === 0) continue;
    const cols = table[0]!.map(classify);
    if (!cols.includes('client')) {
      problems.push(
        `${f + 1}번째 파일: clientId 열이 없습니다 — tools/client-index.mjs --annotate 로 채운 파일(.id.csv)을 올려 주세요`,
      );
      continue;
    }
    for (const [i, cells] of table.slice(1).entries()) {
      count++;
      const get = (c: Col) =>
        cells.map((v, j) => (cols[j] === c ? (v ?? '').trim().replace(/\s+/g, ' ') : '')).filter((v) => v !== '');
      const id = (get('client')[0] ?? '').toLowerCase();
      if (!UUID_RE.test(id)) {
        problems.push(`${f + 1}번째 파일 ${i + 2}줄: clientId 가 없거나 uuid 가 아닙니다(${id || '빈칸'})`);
        continue;
      }
      const cur = byBrand.get(id) ?? { profile: null, topics: [] };
      const businessName = get('business')[0];
      const category = get('category')[0];
      const phrases = [...new Set(get('phrase'))].slice(0, MAX_SEARCH_PHRASES);
      if (businessName && category && phrases.length > 0) {
        cur.profile = { businessName, category, searchPhrases: phrases };
      }
      for (const t of [...get('topic'), ...get('used')]) {
        // 「4번째」 같은 메모 칸은 주제가 아니다
        if (t.length >= 4 && !/^\d+번(째|쨰)?$/.test(t)) cur.topics.push(t.replace(/[?？]+$/, ''));
      }
      byBrand.set(id, cur);
    }
  }
  const items = [...byBrand.entries()].map(([brandId, v]) => ({
    brandId,
    profile: v.profile,
    topics: [...new Set(v.topics)].slice(0, 200),
  }));
  return { items, rows: count, problems };
}
